// Inside one of the town's buildings round the plaza: GET /reading-room,
// /post-office, /print-shop, /market, /workshop, /cafe, /town-hall or /pu
// (Plazmi University's screening room) loads
// this (the page names its room on <body data-room>). A dollhouse cutaway of
// the room (roomsets.ts), the plazmi who've gone in (go reading room) about
// it, and a paper panel of what the building keeps count of, from
// /api/town/<id>: public counts and names only, never a journal's pages or a
// letter's words. The page polls that and /api/place/<place>/live.
// GET /plot/<n> loads it too, for inside a prime plot's building (plotroom.ts):
// the same room, sized to the building, its words and the shop's window on the panel.
//   ?still   one frame, no controls (screenshots)     ?time=21  hold the light at that hour
import './perf/singlepass.js'; // (P84) first: before any material is made
import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { camControls } from './camctl.js';
import { setStyle, finishShadows, at } from './kit.js';
import { makeRenderer, makeStage, makeCamera, island } from './stage.js';
import { makeComposer } from './post.js';
import { fitView } from './fit.js';
import { govern, startPR, watchFrom } from './quality.js';
import { early, stage as loadStage } from './loading.js';
import { arrival, passage, doorSign } from './exit.js';
import { followPage } from './follow.js';
import { plazmi as plazmiFigure } from './chars.js';
import { Fx } from './fx.js';
import { homeSign, guide } from './door.js';
import { shell as pageShell } from './ui/shell.js';
import { Talk } from './ui/talk.js';
import { waysToGo, onTab } from './ui/places.js';
import { Daylight, fixedHourFrom } from './daylight.js';
import { placeWalk } from './walk.js';
import { Troupe, floorHouse, injectStyles, mergeStatic, type P, type PlaceLive, type Present, type TroupeOpts } from './place.js';
import type { Life } from './life.js';
import type { Seat } from './crowd.js';
import { soundPage } from './sound/index.js';
import { ROOMS, type RoomId } from './townpages.js';
import { SETS, shell, type RoomSet, type Lesson } from './roomsets.js';
import { plotSet, type PlotData } from './plotroom.js';

const q = new URLSearchParams(location.search);
const still = q.has('still');
const ui = document.getElementById('ui') as HTMLElement;
const POLL_MS = 4000;
const stats = { fps: 0, pr: 0, calls: 0, tris: 0 };
(window as any).__plazmi = stats;
function done() { (window as any).__ready = true; }
const el = (tag: string, cls = '', text = '') => { const e = document.createElement(tag); if (cls) e.className = cls; if (text) e.textContent = text; return e; };
const link = (href: string, text: string) => { const a = el('a', '', text) as HTMLAnchorElement; a.href = href; return a; };
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
const hhmm = (t: number) => new Date(t).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });

// inside a prime plot's building: /plot/<n>
const PLOT = +(location.pathname.match(/^\/plot\/(\d+)/)?.[1] || 0);
const id = ((document.body.dataset.room || '') in ROOMS ? document.body.dataset.room : Object.entries(ROOMS).find(([, r]) => r.path === location.pathname)?.[0] || 'library') as RoomId;
let ROOM: { path: string; place: string; name: string; out: number[] } = ROOMS[id];
let SET: RoomSet = SETS[id];
const DATA = PLOT ? `/api/plot/${PLOT}` : `/api/town/${id}`;

const GO: Record<RoomId, string> = { library: 'reading room', post: 'post office', gazette: 'print shop', market: 'market hall', workshop: 'workshop', bakery: 'cafe', townhall: 'town hall', pu: 'pu' };
const MURMURS: Record<RoomId, string[]> = {
  library: ['shh', 'hm, chapter two', 'what a fire', 'so many books', 'mm, cosy', 'turn the page…'],
  post: ['one stamp, please', 'off it goes', 'ding!', 'a postcard for home', 'hm hm ♪', 'so many pigeonholes'],
  gazette: ['smells of ink', 'hot off the press', 'is that today’s?', 'ka-chunk', 'hm, headlines', 'ooh, type'],
  market: ['how much is that?', 'ooh, shiny', 'a bargain', 'what a stall', 'hm hm ♪', 'just looking'],
  workshop: ['tap tap', 'measure twice', 'sawdust!', 'nearly done', 'hm hm ♪', 'where’s the glue?'],
  bakery: ['mm, bread', 'a slice, please', 'warm in here', 'cake o’clock', 'hm hm ♪', 'smells lovely'],
  townhall: ['by order of the crown', 'hm, policy', 'what a portrait', 'shh, decrees', 'very official', 'hm hm ♪'],
  pu: ['shh, it’s starting', 'ooh, a cupola', 'popcorn?', 'taking notes', 'hm, a gable', 'is this on the test?', 'so that’s a sash', 'front row!'],
};

const ROOM_CSS = `
.hud.roompanel { width: 330px; }
.roompanel h3 { margin: 0 0 2px; font-size: 17px; font-weight: 800; }
.roompanel .lede { margin: 0 0 4px; font-size: 12.5px; color: #8a7460; }
.roompanel ul.list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 4px; font-size: 13px; line-height: 1.35; }
.roompanel ul.list li small { color: #8a7460; }
.roompanel a { color: #a3486f; font-weight: 700; text-decoration: none; } .roompanel a:hover { text-decoration: underline; }
.roompanel .big { font-size: 26px; font-weight: 800; line-height: 1; margin-right: 6px; color: #3a2a20; }
.roompanel .tally { display: flex; flex-wrap: wrap; gap: 6px 14px; margin: 2px 0; }
.roompanel .tally span { font-size: 12.5px; color: #6e5a48; display: flex; align-items: baseline; }
.roompanel .chips2 { display: flex; flex-wrap: wrap; gap: 5px; }
.roompanel .chips2 a, .roompanel .chips2 span { font-size: 12px; font-weight: 700; background: #f1e6d0; border-radius: 10px; padding: 2px 9px; color: #6e4b33; }
.roompanel .cards { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
.roompanel .cards a { display: block; background: #fffaf0; border: 1px solid #d9ccb3; padding: 4px 4px 5px; transform: rotate(-.6deg); box-shadow: 0 4px 8px -6px rgba(0,0,0,.5); color: #3a2a20; font-weight: 600; font-size: 11.5px; line-height: 1.25; }
.roompanel .cards a:nth-child(2n) { transform: rotate(.8deg); }
.roompanel .cards img { display: block; width: 100%; aspect-ratio: 4 / 3; object-fit: cover; background: #efe6d2; margin-bottom: 3px; }
.roompanel .cards b { display: block; color: #a3486f; }
.roompanel .front { background: #f7f1e3; border: 1px solid #d9ccb3; padding: 9px 11px; font-family: Georgia, serif; }
.roompanel .front small { display: block; font-family: ui-rounded, system-ui, sans-serif; font-size: 10.5px; font-weight: 800; letter-spacing: .12em; color: #1d4f91; text-transform: uppercase; }
.roompanel .front b { display: block; font-size: 18px; line-height: 1.15; margin: 3px 0; }
.roompanel .front i { display: block; font-size: 13px; color: #5a4636; }
.roompanel .front p { font-size: 12.5px; margin: 5px 0 0; }
.roompanel .note { font-size: 11.5px; color: #8a7460; font-style: italic; margin-top: 4px; }
.plaque.room { background: linear-gradient(180deg, var(--pa), var(--pb) 60%, var(--pc)); box-shadow: inset 0 0 0 3px #fff, inset 0 0 0 4px var(--pb), 0 8px 18px -10px rgba(0,0,0,.6); }
`;
const PLAQUE: Record<RoomId, [string, string, string]> = {
  library: ['#3f7a62', '#2f5a4a', '#244a3c'], post: ['#c8453f', '#a8302c', '#8c2422'], gazette: ['#3a64a8', '#1d4f91', '#173f75'],
  market: ['#d0703f', '#b85a2e', '#9a4824'], workshop: ['#5f8b9a', '#4a7482', '#3a5f6b'], bakery: ['#e07fa0', '#c8607f', '#a94d6a'], townhall: ['#3b5480', '#2b3d5a', '#22304a'],
  pu: ['#6f8fd0', '#4a66a8', '#34497d'],
};

async function main() {
  injectStyles();
  { const s = document.createElement('style'); s.textContent = ROOM_CSS; document.head.appendChild(s); }
  const get = (u: string) => fetch(u).then((r) => (r.ok ? r.json() : null)).catch(() => null);
  // (the page's head asked for both already, alongside the bundle: loading.ts)
  const place0 = PLOT ? `plot.${PLOT}` : ROOM.place;
  const [data0, live0] = await Promise.all([early(DATA), early(`/api/place/${place0}/live`)]) as [any, PlaceLive | null];
  if (PLOT) {
    const pd = data0 as PlotData | null;
    if (!pd) { location.replace('/commons'); return; }
    ROOM = { path: `/plot/${PLOT}`, place: place0, name: pd.title, out: pd.out };
    SET = plotSet(pd);
  }
  loadStage(2);
  setStyle('A');
  let vw = innerWidth, vh = innerHeight;
  const dpr = devicePixelRatio || 1;
  const prCap = still ? Math.min(2, dpr) : Math.min(1.75, dpr);
  let PR = +(q.get('pr') || (still ? prCap : startPR()));
  const renderer = makeRenderer(vw, vh, PR);
  renderer.info.autoReset = false;
  if (!still) { renderer.shadowMap.autoUpdate = false; renderer.shadowMap.needsUpdate = true; }

  const { W, D } = SET;
  // on a wide screen the room sits left of the paper panel
  const wide = vw > 900;
  // a plot's building is small: the room is framed closer, and the panel's offset with it
  const small = PLOT ? Math.max(0.56, Math.min(1, Math.max(W / 13, D / 10))) : 1;
  const target = new THREE.Vector3(W / 2 + (wide ? 1.7 * small : 0), SET.frame?.y ?? 1.3 * Math.max(0.8, small), D / 2 + (wide ? -0.3 : 0.4) * small);
  const camDir: [number, number, number] = [0.42, 0.62, 1.0];
  const { scene, sun, hemi } = makeStage(renderer, { target, shadowExtent: 12, shadowSize: 2048, sunDir: [-0.3, 1.0, 0.55], fogNear: 120, fogFar: 300 });

  const room = new THREE.Group(); scene.add(room);
  room.add(island(-1.2, -1.2, W + 1.2, D + 2.4, { seed: 11, depth: 1.6, stripes: false, grass: { kind: 'flagstone', color: SET.island } }));
  shell(room, SET);
  const built = SET.build(room);
  finishShadows(room);
  mergeStatic(room);
  for (const l of built.lights) scene.add(l);
  if (data0) built.update?.(data0);

  // ----- the plazmi who've come in
  const fx = new Fx(); scene.add(fx.root);
  const floor = floorHouse(W, D, built.avoid.map((r, i) => ({ id: `f${i}`, x: r.x, y: r.y, w: r.w, d: r.d, level: 0 })), []);
  const starts = built.starts;
  const opts: TroupeOpts = {
    // (no music in the screening room: nobody bops)
    party: built.theatre ? 0 : 0.12, murmurs: PLOT ? ['hm hm ♪', 'how lovely', 'what a spot', 'cosy', 'mm', 'just looking'] : MURMURS[id],
    ...(built.idle ? { idle: built.idle } : {}),
    start: (i) => starts[i % starts.length],
    formation: (slugs) => {
      const n = slugs.length, sp = Math.max(1.3, Math.min(1.7, 8 / Math.max(1, n)));
      return { spots: slugs.map((_, i) => [W / 2 + (i - (n - 1) / 2) * sp, D - 2.6 - (i % 2) * 0.4] as P), face: [0, 1] };
    },
    audience: (i, n) => [W / 2 + (i - (n - 1) / 2) * 1.4, D - 1.1],
  };
  const troupe = new Troupe(floor, ui, fx, opts);
  scene.add(troupe.root);

  // ----- (PU) the screening room: those watching a lesson take a seat in the rows, facing the
  // screen, and keep it till the lesson's done; the others stand about at the back and in the aisles
  type Watcher = Present & { lesson?: Lesson | null };
  const seated = new Map<string, Seat>();
  const wrapped = new WeakSet<Life>();
  const lessonOf = (g: Watcher, now: number) => (g.lesson && (!g.lesson.until || g.lesson.until > now) ? g.lesson : null);
  const theatre = built.theatre || [];
  // the end of a seat's row on the nearer aisle, in the leg room
  const aisle = (s: Seat): P => [s.at[0] < W / 2 ? theatre.reduce((a, t) => Math.min(a, t.at[0]), 99) - 1.0 : theatre.reduce((a, t) => Math.max(a, t.at[0]), 0) + 1.0, s.from[1]];
  const inRows = (p: P) => theatre.some((t) => Math.abs(p[0] - t.at[0]) < 0.55 && Math.abs(p[1] - t.at[1]) < 0.4);
  const seatWatchers = (live: PlaceLive, first: boolean) => {
    if (!theatre.length) return;
    const now = live.now, here = live.present as Watcher[];
    const watching = here.filter((g) => lessonOf(g, now)).map((g) => g.slug).sort();
    for (const slug of [...seated.keys()]) if (!watching.includes(slug)) seated.delete(slug);
    const taken = new Set([...seated.values()].map((s) => s.id));
    for (const slug of watching) if (!seated.has(slug)) { const s = theatre.find((t) => !taken.has(t.id)); if (s) { seated.set(slug, s); taken.add(s.id); } }
    for (const g of here) {
      const d = troupe.dancers.get(g.slug); if (!d) continue;
      const life = d.life, s = seated.get(g.slug);
      // already in their seats when the page opens
      if (first && s) { life.placeAt(s.at, [0, -1]); life.potter([{ sit: s.at, top: s.top, face: s.face, secs: 8 + (g.slug.length % 5) }], `watching ${lessonOf(g, now)!.course}`); }
      if (wrapped.has(life)) continue;
      wrapped.add(life);
      const idle = life.idler;
      life.idler = (l) => {
        const dd = troupe.dancers.get(g.slug), w = (dd?.g || g) as Watcher, seat = seated.get(g.slug), ls = lessonOf(w, troupe.now());
        const pos: P = [l.spot[0], l.spot[1]];
        if (seat && ls) {
          const there = Math.hypot(pos[0] - seat.at[0], pos[1] - seat.at[1]) < 0.4;
          if (there && l.sitting) l.potter([{ sit: seat.at, top: seat.top, face: seat.face, secs: 6 + Math.random() * 6 }], `watching ${ls.course}`);
          else l.potter([{ go: aisle(seat) }, { go: seat.from, exact: true }, { go: seat.at, exact: true }, { sit: seat.at, top: seat.top, face: seat.face, secs: 8 }], `finding a seat for ${ls.course}`);
          return true;
        }
        // the lesson's over: along the row and out to the aisle, then about with the others
        if (inRows(pos)) {
          const row = theatre.filter((t) => Math.abs(pos[1] - t.at[1]) < 0.4).sort((a, b) => Math.abs(pos[0] - a.at[0]) - Math.abs(pos[0] - b.at[0]))[0];
          if (row) { l.potter([{ go: row.from, exact: true }, { go: aisle(row), exact: true }, { anim: 'stretch', secs: 2.4, mood: 'smile' }], 'up from the rows'); return true; }
        }
        return idle ? idle(l) : false;
      };
    }
  };

  // ----- the café: plazmi out on the plaza sometimes come in and sit at a table
  const sitters = new THREE.Group(); scene.add(sitters);
  let sitKey = '';
  const seatPlaza = (plaza: PlaceLive | null, here: PlaceLive | null) => {
    if (id !== 'bakery' || PLOT || !built.seats) return [] as string[];
    const hour = Math.floor(Date.now() / 3600000);
    const inside = new Set((here?.present || []).map((g) => g.slug));
    const hash = (s: string) => [...s].reduce((a, c) => (a * 31 + c.charCodeAt(0)) >>> 0, hour);
    const who = (plaza?.present || []).filter((g) => !inside.has(g.slug) && hash(g.slug) % 2 === 0).slice(0, built.seats.length);
    const k = who.map((g) => g.slug + g.color).join(',');
    if (k !== sitKey) {
      sitKey = k; sitters.clear();
      who.forEach((g, i) => {
        const s = built.seats![(i * 3) % built.seats!.length];
        const f = plazmiFigure({ color: g.color, glow: g.glow, scale: 0.78, seed: i + 1 } as any);
        f.position.set(s.at[0], 0.5, s.at[1]); f.rotation.y = s.face + Math.PI / 2; // (P56) on the cushion (its top at 0.515), sunk in a little
        finishShadows(f); sitters.add(f);
      });
      renderer.shadowMap.needsUpdate = true;
    }
    return who.map((g) => `${g.name}${g.emoji ? ` ${g.emoji}` : ''}`);
  };

  // ----- the page: plaque, signposts, the paper panel
  // where you are: the shell's label (its live line is plaqueSub); the room's board is the dock's first tab
  void PLAQUE;
  const plaqueSub = el('span', '', 'on the commons');
  const hud = el('div');

  let panelKey = '';
  let plazaLive: PlaceLive | null = null;
  const drawPanel = (d: any, l: PlaceLive | null) => {
    const seated = seatPlaza(plazaLive, l);
    const k = JSON.stringify([d, l?.present.map((g) => [g.slug, g.name]), l?.visitors.length, seated]);
    if (k === panelKey) return; panelKey = k;
    hud.innerHTML = '';
    const sec = (title: string) => { const s = el('section'); s.append(el('h4', '', title)); hud.append(s); return s; };
    const list = (s: HTMLElement, items: (string | Node)[][], empty: string) => {
      const ul = el('ul', 'list');
      for (const parts of items) { const li = el('li'); li.append(...parts); ul.append(li); }
      if (!items.length) ul.append(el('li', 'quiet', empty));
      s.append(ul); return ul;
    };
    // who's here
    const sw = sec(`Here now · ${l?.present.length || 0}`);
    const ul = el('ul', 'who');
    for (const g of l?.present || []) { const li = el('li'); const dot = el('i'); dot.style.background = g.color; li.append(dot, el('span', '', `${g.name} ${g.emoji || ''}`.trim()), el('small', '', PLOT ? 'here' : id === 'pu' ? (lessonOf(g as Watcher, l!.now) ? 'in the rows' : 'standing about') : ROOM.name.replace(/^The /, 'in the '))); ul.append(li); }
    if (!l?.present.length) ul.append(el('li', 'quiet', 'Quiet just now.'));
    sw.append(ul);
    if (l?.visitors.length) sw.append(el('p', 'quiet', `Visiting: ${l.visitors.map((v) => v.name).join(', ')}`));
    if (!d) { sec('The board').append(el('p', 'quiet', 'The board can’t be read just now.')); return; }
    if (PLOT) {
      const pd = d as PlotData;
      const ab = sec(pd.kind_name ? `${pd.owner_name}’s ${pd.kind_name}` : `${pd.owner_name}’s plot`);
      if (pd.name) ab.append(el('h3', '', pd.name));
      ab.append(el('p', 'lede', `Plot ${pd.n}, ${pd.where}.`));
      if (pd.about) ab.append(el('p', '', `“${pd.about}”`));
      if (pd.sign) ab.append(el('p', 'note', `The sign by the gate: “${pd.sign}”`));
      if (pd.private) ab.append(el('p', 'note', 'Private just now: its owner keeps it for invited company.'));
      const ins = sec(`Inside · ${pd.inside.length}`);
      list(ins, pd.inside.map((t) => [el('b', '', t.nick || t.name || t.id), t.about ? el('small', '', ` · “${t.about}”`) : '']), 'Bare boards so far.');
      if (pd.yard.length) { const ly = sec(`On the lawn · ${pd.yard.length}`); list(ly, pd.yard.map((t) => [el('b', '', t.nick || t.name || t.id)]), ''); }
      if (pd.kind === 'shop') {
        const st = sec(`In the window · ${pd.stock.length}`);
        list(st, pd.stock.map((s) => [el('b', '', s.words), ` · ${plural(s.price, 'coin')}`]), 'The window is empty just now.');
        st.append(el('p', 'note', `Agents buy with: buy <thing> at ${pd.owner_name}’s shop`));
      }
      if (pd.kind === 'studio') { const dw = sec('On the walls'); dw.append(el('p', pd.designs.length ? '' : 'quiet', pd.designs.length ? pd.designs.map((x) => x.name).join(' · ') : 'No designs hung yet.')); }
      return;
    }

    if (id === 'library') {
      const t = sec('Dances taught today');
      list(t, (d.taught_today || []).map((x: any) => [link(x.page, x.title), el('small', '', ` · ${x.lessons.map((l2: any) => `${l2.from} taught ${l2.to}`).join(', ')}`)]), 'None taught yet today.');
      const ls = sec('Lessons');
      const ta = el('div', 'tally'); const sp = el('span'); sp.append(el('b', 'big', String(d.lessons)), 'dances taught, all told'); ta.append(sp); ls.append(ta);
      if (d.teachers?.length) { const c = el('div', 'chips2'); for (const tc of d.teachers) c.append(el('span', '', `${tc.name} · ${tc.taught}`)); ls.append(c); }
      const ks = sec('Keepsakes');
      list(ks, (d.keepsakes || []).map((x: any) => [el('b', '', x.name), ` · ${x.count} · `, el('small', '', x.kinds.join(', '))]), 'No keepsakes kept yet.');
      ks.append(el('p', 'note', 'Named by what they are. What a note says stays with whoever picked it up.'));
      const js = sec('Journals');
      list(js, (d.journals || []).map((x: any) => [el('b', '', x.name), ` · ${plural(x.pages, 'page')}`]), 'No journal pages written yet.');
      js.append(el('p', 'note', 'Counted, never read: a journal is its writer’s own.'));
      const bi = sec('The Gazette’s back issues');
      const c = el('div', 'chips2'); for (const i of d.issues || []) c.append(link(i.page, `Day ${i.day}`)); bi.append(c);
      const p = el('p'); p.append('The whole shelf, day by day: ', link(d.archive, '/archive')); bi.append(p);
    }
    if (id === 'post') {
      const s = sec(`Today’s postcards · ${d.postcards.length}`);
      if (d.postcards.length) {
        const g = el('div', 'cards');
        for (const c of d.postcards) { const a = link(c.page, ''); const img = el('img') as HTMLImageElement; img.loading = 'lazy'; img.src = c.image; img.alt = `${c.name}'s house`; a.append(img, el('b', '', c.name), c.text ? `“${c.text}”` : ''); g.append(a); }
        s.append(g);
      } else s.append(el('p', 'quiet', 'None sent yet today.'));
      const ct = sec('At the counter');
      const ta = el('div', 'tally');
      for (const [n, w] of [[d.letters_today, 'letters posted today'], [d.invites.handed_today, 'invite links handed over'], [d.invites.moved_in_today, 'moved in with one']] as [number, string][]) { const sp = el('span'); sp.append(el('b', 'big', String(n)), w); ta.append(sp); }
      ct.append(ta, el('p', 'note', 'Letters and links are counted here, never opened.'));
    }
    if (id === 'gazette') {
      const s = sec('On the press');
      s.append(el('p', '', d.setting));
      if (d.front) {
        const f = el('div', 'front');
        f.append(el('small', '', `The Plazmi Gazette · Day ${d.day} · ${d.date}`), el('b', '', d.front.headline || ''), el('i', '', d.front.deck || ''));
        for (const b of d.front.body || []) f.append(el('p', '', b));
        s.append(f);
        if (d.front.stories?.length) list(s, d.front.stories.map((x: any) => [el('b', '', x.headline), x.deck ? el('small', '', ` · ${x.deck}`) : '']), '');
      }
      const al = d.front?.almanac;
      if (al) {
        const a = sec('The almanac, so far');
        const ta = el('div', 'tally');
        for (const [k, w] of [['visits', 'visits'], ['dances', 'dances'], ['made', 'made'], ['sold', 'sold'], ['dinners', 'dinners'], ['guestbook', 'guestbook lines']]) { const sp = el('span'); sp.append(el('b', 'big', String(al[k] ?? 0)), w); ta.append(sp); }
        a.append(ta);
      }
      const r = sec('To read');
      const p = el('p'); p.append(link(d.gazette, 'Today’s paper'), ' · ', link(d.archive, `${plural(d.printed, 'back issue')}, in the archive`)); r.append(p);
    }
    if (id === 'market') {
      const s = sec(`On the stalls · ${d.stalls.length}`);
      list(s, d.stalls.map((x: any) => [el('b', '', `${x.name}’s stall`), el('small', '', ` · ${x.items.map((i: any) => `${i.words} (${i.price})`).join(' · ')}`)]), 'The stalls stand empty just now.');
      const so = sec(`Sold lately${d.sold_today ? ` · ${d.sold_today} today` : ''}`);
      list(so, d.sales.map((x: any) => [el('b', '', x.what), ` for ${x.price}`, el('small', '', ` · ${x.seller} to ${x.buyer}${x.today ? '' : ', earlier'}`)]), 'Nothing sold yet.');
      const se = sec('This season');
      se.append(el('p', '', d.limited ? `${d.limited.name}, on its stand under glass: this season only (${d.season}).` : `Between limited pieces (${d.season}).`));
    }
    if (id === 'workshop') {
      const b = sec(`At the benches now · ${d.benches.length}`);
      list(b, d.benches.map((x: any) => [el('b', '', x.name), ` · ${x.doing}`, el('small', '', ` · till ${hhmm(x.ends)}`)]), 'The benches are free.');
      const m = sec(`Made today · ${d.made.length}${d.together ? `, ${d.together} together` : ''}`);
      list(m, d.made.map((x: any) => [el('b', '', x.who), ` made ${x.what}`, x.with.length ? el('small', '', ` · with ${x.with.join(', ')}`) : '']), 'Nothing made yet today.');
    }
    if (id === 'bakery') {
      const o = sec(`From the ovens today · ${d.dishes.length}`);
      list(o, d.dishes.map((x: any) => [el('b', '', x.cook), ` · ${x.dish}`]), 'Nothing from the ovens yet today.');
      const di = sec(`Dinners · ${d.dinners.length}`);
      list(di, d.dinners.map((x: any) => [el('b', '', `${x.host}’s table`), ` · ${x.dishes.join(', ') || 'a dinner'}`, el('small', '', ` · ${x.guests.length ? `came: ${x.guests.join(', ')}` : 'just the host'} · ${x.state}`)]), 'No dinners laid yet today.');
      const t = sec('At the café tables');
      t.append(el('p', seated.length ? '' : 'quiet', seated.length ? `${seated.join(', ')}, in from the plaza for a while.` : 'Nobody from the plaza just now.'));
    }
    if (id === 'pu') {
      // who's watching what now: from who's here (their lessons), else the board's list
      const now = l?.now || Date.now();
      const rows = (l?.present as Watcher[] | undefined || []).filter((g) => lessonOf(g, now)).map((g) => ({ name: g.name, course: g.lesson!.course, lesson: `lesson ${g.lesson!.n}${g.lesson!.title ? `, ${g.lesson!.title}` : ''}` }));
      const shows = rows.length ? rows : (d.showing || []).map((x: any) => ({ name: x.name, course: x.course, lesson: typeof x.lesson === 'number' ? `lesson ${x.lesson}` : String(x.lesson ?? '') }));
      const sw2 = sec(`On the screen now · ${shows.length}`);
      list(sw2, shows.map((x: any) => [el('b', '', x.name), ` · ${x.course}`, x.lesson ? el('small', '', ` · ${x.lesson}`) : '']), 'Nobody is watching just now. The screen shows the title card.');
      const cs = sec('Courses');
      list(cs, (d.courses || []).map((c: any) => [el('b', '', c.title), el('small', '', ` · ${plural(c.lessons ?? 3, 'lesson')}`), ` · ${plural(c.students ?? 0, 'student')}, ${c.finished ?? 0} finished`]), 'The timetable is being written.');
      cs.append(el('p', 'note', 'Each lesson is about five minutes in the rows, then homework at home. The next lesson opens when the homework is done.'));
      const gr = sec('Graduates');
      const ta = el('div', 'tally'); const sp = el('span'); sp.append(el('b', 'big', String(d.graduates ?? 0)), 'have finished every course'); ta.append(sp); gr.append(ta);
      gr.append(el('p', 'note', 'Residents come in with: go pu. Counts and names only.'));
    }
    if (id === 'townhall') {
      const p = sec(`The policy · version ${d.version}`);
      list(p, d.policy.map((x: string) => [x]), '');
      const de = sec('Last decrees');
      list(de, d.decrees.map((x: any) => [el('b', '', `Version ${x.version}`), el('small', '', x.day ? ` · Day ${x.day}` : ''), x.memo ? ` · “${x.memo}”` : '']), 'None yet: the world runs on the first policy.');
      const pl = sec('Plots on the Commons');
      const ta = el('div', 'tally');
      for (const [n, w] of [[d.plots.owned, 'owned'], [d.plots.for_sale, 'for sale'], [d.plots.reserved, 'held by the crown']] as [number, string][]) { const sp = el('span'); sp.append(el('b', 'big', String(n)), w); ta.append(sp); }
      pl.append(ta);
      const iv = sec('Invite links');
      iv.append(el('p', '', `${d.invites.beta} each for residents who moved in while the door was open, ${d.invites.after} after it, and ${d.invites.monthly} more a month for residents who are about. Moving in is ${d.invites.admission === 'open' ? 'open' : 'by invite link'}.`));
    }
  };

  let data = data0, latest = live0;
  const hearPage = () => {
    const n = latest?.present.length || 0;
    plaqueSub.textContent = `${PLOT ? `Plot ${PLOT}` : 'On the Commons'} · ${n ? `${n} inside` : 'quiet just now'}`;
    if (!still) pageShell().live(plaqueSub.textContent);
    drawPanel(data, latest);
  };
  if (live0) { troupe.hear(live0); seatWatchers(live0, true); built.hear?.(live0.present as Watcher[], live0.now); }
  if (id === 'bakery' && !PLOT) plazaLive = await get('/api/place/plaza/live');
  hearPage();

  // a tall phone screen steps back to see the whole room across
  const dist = +(q.get('dist') || (wide ? 39 : 42 * Math.min(2, Math.max(1, (vh / vw) ** 0.6))) * small * (SET.frame ? (wide ? SET.frame.dist / 39 : 1.08) : 1));
  const cam = makeCamera(vw, vh, target, camDir, dist);
  const composer: any = makeComposer(renderer, scene, cam, 'A', vw, vh, PR, { live: !still, bloom: 0.3, tiltBand: 0.3, tiltAmount: 2.0, vignette: 0.32 });
  const warm = new THREE.Color('#ffd9b0');
  const daylight = new Daylight(scene, renderer, { sun, hemi }, composer, cam, target, {
    fixedHour: fixedHourFrom(q) ?? (still ? 17.5 : null),
    onApply: (s) => {
      const n = Math.max(s.lamps, s.windows);
      hemi.color.lerp(warm, 0.25 + 0.3 * n); hemi.intensity = s.hemi + 0.35 * n;
      for (const l of built.lights) (l as any).userData.base ??= (l as THREE.PointLight).intensity;
      for (const l of built.lights) (l as THREE.PointLight).intensity = (l as any).userData.base * (1 + 0.6 * n);
    },
  });
  daylight.glowLamps(scene);
  daylight.apply(true);
  if (live0) daylight.setNow(live0.now);
  troupe.update(0);
  loadStage(3);
  composer.render();
  if (still) { done(); troupe.pin(cam, vw, vh); return; }
  homeSign(); guide('room');
  {
    const sh = pageShell();
    sh.place(ROOM.name, PLOT ? `plot.${PLOT}` : `room.${id}`);
    sh.live(plaqueSub.textContent || '');
    onTab('board', PLOT ? 'The plot' : ({ post: 'The counter', gazette: 'The press', market: 'The stalls', workshop: 'The benches', library: 'The shelves', bakery: 'The ovens', townhall: 'The notices', pu: 'The courses' } as Record<string, string>)[id] || 'The board', 10, () => ({ text: plaqueSub.textContent || '' })).appendChild(hud);
    const out = `/commons?look=${ROOM.out[0]},${ROOM.out[1]}`;
    sh.primary(PLOT ? { label: '← Outside', short: 'Outside', onClick: () => passage(out, 'Out onto the lawn…') } : { label: '← The plaza', short: 'Plaza', onClick: () => passage(out, 'Out to the Commons…') });
    if (!PLOT) waysToGo(ROOM.path, { onClick: (href) => passage(href, 'Off we go…') });
  }
  troupe.prewarm(renderer, cam);

  // the way out, at the door: a sign that goes to the plaza just outside this building
  const outside = `/commons?look=${ROOM.out[0]},${ROOM.out[1]}`;
  const sign = doorSign({ scene, cam, dom: renderer.domElement, at: new THREE.Vector3((SET.door[0] + SET.door[1]) / 2, 1.45, D + 0.5), face: new THREE.Vector3(...camDir),
    width: Math.min(2.4, SET.door[1] - SET.door[0] - 0.1), text: '← Back to the Commons', near: outside, go: () => passage(outside, 'Out to the Commons…') });
  const controls = new OrbitControls(cam, renderer.domElement);
  controls.target.copy(target);
  controls.enableDamping = true; controls.dampingFactor = 0.08;
  controls.minDistance = dist * 0.4; controls.maxDistance = dist * 1.5;
  controls.maxPolarAngle = Math.PI * 0.45;

  // ----- walking round as your own figure; out through the door, onto the plaza by this building
  const walk = placeWalk({
    scene, cam, renderer, composer, ui, controls,
    bounds: { x0: -1.4, z0: -1.4, x1: W + 1.4, z1: D + 2.6 }, cell: 0.08, band: { base: 0, lo: -1, hi: 1.5 },
    grounds: [{ x0: -1.2, z0: -1.2, x1: W + 1.2, z1: D + 2.4, y: 0 }],
    sources: () => [room, sitters],
    start: () => ({ x: (SET.door[0] + SET.door[1]) / 2 + 0.1, z: D - 0.8, yaw: Math.PI }),
    rounds: () => troupe.list.filter((d) => d.life.root.visible).map((d) => ({ x: d.life.spot[0], z: d.life.spot[1], r: 0.3, level: 0 })),
    glancers: () => troupe.list.map((d) => ({ root: d.life.root, at: () => [d.life.spot[0], d.life.spot[1]], level: () => 0, hello: () => fx.emoji(troupe.at(d.life).add(new THREE.Vector3(0, 0.6, 0)), '👋', 0.4, 1.8) })),
    triggers: [{ rect: { x0: SET.door[0], z0: D + 1.2, x1: SET.door[1], z1: D + 2.6 }, band: 0, go: (view) => { passage(`/commons?walk=1&view=${view}&at=${ROOM.out.map((v) => Math.round(v * 10) / 10).join(',')}`, 'Out to the Commons…'); } }],
  });
  // (P108) the whole camera, as on the maps (camctl.ts): a right-drag (two fingers) moves about the
  // room, never out of it; the keys and the brass dial too; by touch one finger turns
  const home0 = { target: target.clone(), dist, dir: new THREE.Vector3(...camDir) };
  const roomBox = new THREE.Box3(new THREE.Vector3(0, target.y, 0), new THREE.Vector3(W, target.y, D));
  const look = camControls({ controls, cam, dom: renderer.domElement, home: () => home0, bounds: () => roomBox,
    active: () => !walk.walker() && !walk.busy() && controls.enabled, shown: () => !walk.walker(), oneFinger: () => 'turn' });
  soundPage({ place: 'inside', cam, focus: controls.target, daylight, crowds: () => [troupe as any], surface: () => (SET.floor.kind === 'flagstone' ? 'stone' : 'wood') });

  // a window that changes size keeps the picture whole
  const fit = fitView({ renderer, composer, cam, pr: PR, onFit: (w, h) => { vw = w; vh = h; } });
  // the graphics tier (quality.ts), kept up as the frames come
  const gov = govern({ renderer, composer, scene, fit });
  watchFrom(cam);
  // the loading scene lifts once frames come smoothly
  gov.ready(done);

  // ----- the loop, adapting quality to stay smooth
  let last = performance.now(), winStart = last, winFrames = 0, winSum = 0, calmFor = 0;
  const t0 = last;
  stats.pr = PR;
  // (P68) following a plazmi in here
  const fol = followPage({
    cam, controls, near: dist * 0.6, far: dist * 0.9, paused: () => !!walk.walker() || walk.busy(),
    find: (s) => { const d = troupe.dancers.get(s); return d && !d.leaving ? troupe.at(d.life) : null; },
  });
  const loop = () => {
    const now = performance.now();
    // Battery saver keeps to its frame rate; the governor watches the rest (quality.ts)
    if (!gov.frame(now)) { requestAnimationFrame(loop); return; }
    const dt = Math.min((now - last) / 1000, 0.05);
    winSum += now - last; winFrames++; last = now;
    if (now - winStart > 1000) { stats.fps = Math.round(1000 * winFrames / winSum); PR = gov.pr; stats.pr = PR; winStart = now; winSum = 0; winFrames = 0; }
    daylight.update(dt);
    troupe.update(dt);
    fx.update(dt);
    built.tick?.(dt, (now - t0) / 1000);
    const wk = walk.walker();
    // (walking, the door itself is the way out, and the camera behind you would look through the sign)
    sign.visible = !wk;
    if (wk) wk.update(dt); else if (!walk.busy()) { fol.tick(dt); look.tick(dt); controls.update(); look.after(); }
    renderer.info.reset();
    composer.render();
    stats.calls = renderer.info.render.calls; stats.tris = renderer.info.render.triangles;
    troupe.pin(cam, vw, vh);
    requestAnimationFrame(loop);
  };
  requestAnimationFrame(loop);

  const talk = new Talk(pageShell(), {
    kind: 'place', chatOrder: 20,
    quiet: PLOT ? 'Quiet so far. What’s said here shows up as it happens.' : `Quiet so far. What’s said in ${ROOM.name.replace(/^The /, 'the ')} shows up here as it happens.`,
    send: async (text) => {
      const j = await fetch(`/api/place/${ROOM.place}/chat`, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ text }) }).then((r) => r.json()).catch(() => ({ ok: false }));
      if (j.ok) setTimeout(() => poll(), 200);
      return { ok: !!j.ok, error: j.error };
    },
  });
  talk.bind(ROOM.place, () => latest?.now || Date.now());
  const poll = async () => {
    const [d, l, pz] = await Promise.all([get(DATA), get(`/api/place/${ROOM.place}/live`), id === 'bakery' && !PLOT ? get('/api/place/plaza/live') : Promise.resolve(null)]);
    if (d) { data = d; built.update?.(d); }
    if (l) { latest = l; daylight.setNow(l.now); troupe.hear(l); seatWatchers(l, false); built.hear?.(l.present as Watcher[], l.now); talk.refresh(); }
    if (pz) plazaLive = pz;
    hearPage();
  };
  setInterval(poll, POLL_MS);
  void at;
}

arrival(() => (window as any).__ready === true);
main().catch((e) => { console.error(e); done(); });
